import { useState, useMemo, useEffect } from 'react'
import { usePlanProyectos } from '../usePlanProyectos'
import { supabase } from '@/lib/supabase/cliente'
import {
  calcularDevengadoProyectos,
  resumirPeriodoPyg,
  generarFichasProyectosPyg,
  etiquetaMes,
} from './devengado'
import type { RangoPyg, AvanceHistorial } from './tipos'
import { BarraControlPyg } from './BarraControlPyg'
import { TarjetasKpiPyg } from './TarjetasKpiPyg'
import { GraficoProduccionPyg } from './GraficoProduccionPyg'
import { TablaResultadosMensual } from './TablaResultadosMensual'
import { CruceProyectosRentabilidad } from './CruceProyectosRentabilidad'
import { useActasCorte } from '../cortes/useActasCorte'
import estilos from './PygProyectos.module.css'

export function PygProyectosPage({ proyectoIdInicial = null }: { proyectoIdInicial?: string | null }) {
  const { proyectos } = usePlanProyectos()
  const { todasLasActas } = useActasCorte()
  const hoy = useMemo(() => new Date(), [])
  const mesDeHoy = useMemo(() => {
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`
  }, [hoy])

  const [proyectoId, setProyectoId] = useState<string | null>(proyectoIdInicial)
  const [mesActualClave, setMesActualClave] = useState<string>(mesDeHoy)
  const [rango, setRango] = useState<RangoPyg>('mes')
  const [historialAvances, setHistorialAvances] = useState<AvanceHistorial[]>([])

  // Intentar cargar historial de la base de datos si existe la tabla
  useEffect(() => {
    async function cargarHistorial() {
      try {
        const { data, error } = await (supabase as any)
          .from('plan_avances_historial')
          .select('*')
          .order('created_at', { ascending: true })

        if (!error && data && data.length > 0) {
          const mapeados: AvanceHistorial[] = data.map((d: any) => ({
            id: d.id,
            modulo_id: d.modulo_id,
            proyecto_id: d.proyecto_id,
            avance_previo: Number(d.avance_previo) || 0,
            avance_nuevo: Number(d.avance_nuevo) || 0,
            delta: Number(d.delta) || 0,
            created_at: d.created_at,
            mes: (d.created_at as string).slice(0, 7),
          }))
          setHistorialAvances(mapeados)
        }
      } catch {
        // Fallback analítico silencioso
      }
    }
    cargarHistorial()
  }, [])

  // 1. Calcular serie mensual devengada
  const meses = useMemo(() => {
    return calcularDevengadoProyectos({
      hoy,
      proyectos,
      historialAvances,
      proyectoIdSeleccionado: proyectoId,
      actasCorte: todasLasActas,
      costoIndirectoTotalMes: 15_000_000,
    })
  }, [hoy, proyectos, historialAvances, proyectoId, todasLasActas])

  const mesesClaves = useMemo(() => meses.map((m) => m.clave), [meses])

  // Ajustar mes seleccionado si quedó fuera del rango
  useEffect(() => {
    if (mesesClaves.length > 0 && !mesesClaves.includes(mesActualClave)) {
      if (mesesClaves.includes(mesDeHoy)) {
        setMesActualClave(mesDeHoy)
      } else {
        setMesActualClave(mesesClaves[mesesClaves.length - 1])
      }
    }
  }, [mesesClaves, mesActualClave, mesDeHoy])

  // 2. Resumen del periodo según el rango seleccionado
  const resumen = useMemo(() => {
    return resumirPeriodoPyg(meses, mesActualClave, rango)
  }, [meses, mesActualClave, rango])

  // Etiqueta legible del periodo
  const etiquetaPeriodo = useMemo(() => {
    if (rango === 'mes') return etiquetaMes(mesActualClave)
    if (rango === 'trim') return `Último trimestre a ${etiquetaMes(mesActualClave)}`
    if (rango === 'ano') return `Último año a ${etiquetaMes(mesActualClave)}`
    return 'Histórico acumulado'
  }, [rango, mesActualClave])

  // 3. Fichas individuales de cada proyecto
  const fichas = useMemo(() => {
    return generarFichasProyectosPyg(proyectos, hoy)
  }, [proyectos, hoy])

  if (proyectos.length === 0) {
    return (
      <div className={estilos.contenedorPyg}>
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
          No hay proyectos registrados para calcular el estado de resultados.
        </div>
      </div>
    )
  }

  return (
    <div className={estilos.contenedorPyg}>
      {/* 1. Barra de Control con Selectores */}
      <BarraControlPyg
        proyectos={proyectos}
        proyectoIdSeleccionado={proyectoId}
        onSeleccionarProyecto={setProyectoId}
        mesesDisponibles={mesesClaves}
        mesActualClave={mesActualClave}
        onSeleccionarMes={setMesActualClave}
        rango={rango}
        onCambiarRango={setRango}
      />

      {/* 2. Tarjetas KPI de Impacto (Arkano PGR) */}
      <TarjetasKpiPyg
        resumen={resumen}
        etiquetaPeriodo={etiquetaPeriodo}
      />

      {/* 3. Gráfico SVG de Producción Mensual y Utilidad */}
      <GraficoProduccionPyg
        meses={meses}
        mesActualClave={mesActualClave}
      />

      {/* 4. Tabla de Estado de Resultados Mes a Mes */}
      <TablaResultadosMensual
        meses={meses}
        mesActualClave={mesActualClave}
      />

      {/* 5. Cruce de Rentabilidad por Proyecto */}
      {!proyectoId && (
        <CruceProyectosRentabilidad
          fichas={fichas}
          onSeleccionarProyecto={(id) => setProyectoId(id)}
        />
      )}
    </div>
  )
}
